import type { SplitLine } from "building-types/floors/floorSplitDataTypes";
import { WindingDir } from "geom/core";
import type { Segment } from "geom/polyclip/dcel";
import { dcelFromPolygons } from "geom/polyclip/dcelFromPolygons";
import { PolygonRoundingMode, polygonUnion, polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import { setContains, setOpByPredicatePolygons } from "geom/polyclip/setOps";
import { polygonToFaceLoops } from "geom/polyclip/trimFaceWithPlane";
import { polyLine2Close, polyLine2Copy, polyLine2SetCCW, polyLine2WindingDir } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

/**
 * Unions a set of room outlines into their combined perimeter(s) via polygon union.
 *
 * Pure computational geometry, no building-generation-pipeline coupling -- moved here from
 * `building-generation/generationTools/generateFloorSystemBoundaryTools.ts` so it's reachable from
 * `doc`-level cascades (e.g. reconciling floor bays after a room delete) without pulling in the
 * broader generation pipeline.
 */
export function roomOutlinesToUnionPerimeters(roomOutlines: PolyLine2[]): PolyLine2[] {
  if (roomOutlines.length === 0) {
    return [];
  }
  const closed = roomOutlines.map((pl) => polyLine2Close(pl));
  const pls = closed.map((pl) => [pl]);
  const unioned = polygonUnion({
    pls,
    roundingMode: PolygonRoundingMode.SNAP_ROUND,
  });
  return unioned
    .map((poly) => poly[0])
    .filter((p): p is PolyLine2 => p !== undefined && p !== null && p.length > 0)
    .map((p) => polyLine2SetCCW(polyLine2Copy(p)));
}

function splitLineToSegments(splitLine: SplitLine): Segment[] {
  const pl = splitLine.polyline;
  const segments: Segment[] = [];
  for (let i = 0; i < pl.length - 2; i += 2) {
    segments.push([pl[i], pl[i + 1], pl[i + 2], pl[i + 3]]);
  }
  return segments;
}

/**
 * Splits a perimeter polygon with a set of split line polylines using DCEL, returning the
 * resulting child polygons (CCW-wound). Moved here alongside `roomOutlinesToUnionPerimeters` for
 * the same reason -- pure geometry with no building-generation-pipeline coupling.
 */
export function splitPerimeterBySplitLines(perimeter: PolyLine2, splitLines: SplitLine[]): PolyLine2[] {
  const allSegments: Segment[] = [];
  const segmentPolygonIds: number[] = [];
  for (let i = 0; i < splitLines.length; i++) {
    const segs = splitLineToSegments(splitLines[i]);
    for (const seg of segs) {
      allSegments.push(seg);
      segmentPolygonIds.push(i + 1);
    }
  }

  if (allSegments.length === 0) {
    return [perimeter];
  }

  const ORIGINAL_FACE_ID = 0;
  const dcel = dcelFromPolygons({
    polygons: [[polyLine2ToPolyClipPolygonLoop(perimeter, false, true, WindingDir.CW)]],
    segments: allSegments,
    segmentPolygonIds,
  });

  const allSegmentIds = Array.from(new Set(segmentPolygonIds));
  const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
    const face = dcel.halfEdgeToFace[halfEdgeIndex];
    const polygons = dcel.faceToPolygons[face];
    return setContains(polygons, [ORIGINAL_FACE_ID]) && setContains(polygons, allSegmentIds);
  });

  const subPolygons = polys
    .map((poly) => polygonToFaceLoops(poly))
    .flat()
    .map((pl) => (polyLine2WindingDir(pl) !== WindingDir.CCW ? polyLine2SetCCW(pl) : pl));

  return subPolygons.length > 0 ? subPolygons : [perimeter];
}
